// How the Host nudges page reads: the status chip on each row, the plain-words
// summary of the settings, and the labels the dialogs share with the table.
// Pure formatting, no Firebase, so it can be tested on its own.

import { timeAgo } from "../Events/outreachFormat";

// Every reason the backend can give for not texting a host (REASONS in
// src/hosts/cadence/constants.js), in the operator's words.
export const REASON_LABELS = {
  disabled: "Nudges are off",
  admin_off: "Off by admin",
  not_set_up: "Setup not finished",
  host_opted_out: "Off by host",
  snoozed: "Snoozed",
  no_phone: "No phone",
  texts_off: "Texts off",
  super_admin_skipped: "Admin account",
  cooldown: "Texted recently",
  has_runway: "Booked far enough ahead",
  unanswered_cap: "Paused, no reply",
  due: "Due",
};

export function reasonLabel(reason) {
  return REASON_LABELS[reason] || "Not due";
}

const CHIP = {
  due: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",
  wouldBe: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  paused: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  off: "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300",
  blocked: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
};

/** A date, short: "Oct 12". */
export function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "";
}

/**
 * One row's status. A row that would be texted reads "Due" only while the
 * feature is on; with the master switch off it says "Would be due", because
 * nothing is being sent and the table must not imply otherwise.
 */
export function statusChip(row, enabled) {
  const reason = row?.evaluation?.reason;
  if (row?.evaluation?.due) {
    return enabled
      ? { label: "Due", className: CHIP.due }
      : { label: "Would be due", className: CHIP.wouldBe };
  }
  if (reason === "unanswered_cap") {
    const n = row?.consecutiveUnanswered || 0;
    return { label: `Paused, ${n} unanswered`, className: CHIP.paused };
  }
  if (reason === "snoozed") {
    const until = shortDate(row?.snoozedUntil);
    return { label: until ? `Snoozed until ${until}` : "Snoozed", className: CHIP.off };
  }
  if (reason === "cooldown") {
    const when = timeAgo(row?.lastNudgedAt);
    return { label: when ? `Texted ${when}` : "Texted recently", className: CHIP.off };
  }
  if (reason === "no_phone" || reason === "texts_off") {
    return { label: reasonLabel(reason), className: CHIP.blocked };
  }
  return { label: reasonLabel(reason), className: CHIP.off };
}

/** What the per-host switch means, spelled out. */
export function modeLabel(mode) {
  if (mode === "on") return "Always on";
  if (mode === "off") return "Never";
  return "Follows the cadence";
}

/** The send hour as a clock time: 10 -> "10:00 AM PT". */
export function hourLabel(hour) {
  if (!Number.isInteger(hour)) return "";
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${h12}:00 ${hour < 12 ? "AM" : "PM"} PT`;
}

/**
 * The settings in one sentence, for the save confirmation and each history row.
 */
export function describeConfig(cfg) {
  if (!cfg) return "No settings";
  const parts = [
    cfg.enabled ? "On" : "Off",
    `every ${cfg.cadenceDays} days`,
    cfg.runwayDays > 0
      ? `only when nothing is booked past ${cfg.runwayDays} days out`
      : "whatever is already booked",
    `stop after ${cfg.maxUnanswered} with no reply`,
    `plan ${cfg.horizonDays} days ahead`,
    `at most ${cfg.dailyCap} a day`,
    `sent at ${hourLabel(cfg.sendHour)}`,
  ];
  return parts.join(", ");
}

/** "Sent 4, led to 2 events" for the table. */
export function resultLine(row) {
  const sent = row?.nudgeCount || 0;
  if (!sent) return "Never texted";
  const answered = row?.answeredCount || 0;
  return `Texted ${sent}, led to ${answered} event${answered === 1 ? "" : "s"}`;
}
